import React, { useState } from 'react';
import styled from 'styled-components';
import { Col, Row, FormControl } from 'react-bootstrap';
import Pagination from 'react-js-pagination';
import PropTypes from 'prop-types';
import { CSSTransitionGroup } from 'react-transition-group';
import { useSelector } from 'react-redux';
import BooleanFilter from './Filters/BooleanFilter';
import useDebounce from '../../hooks/useDebounce';
import PlainButton from '../PlainButton';
import { boxShadow, green } from '../../styles/constants';
import IsRelevantFilter from './Filters/IsRelevantFilter';
import { selectInteractibleplantsByBeeId } from '../../ducks/bees';

const SemiBold = styled.span`
  font-weight: 600;
`;

const StyledReactCSSTransitionGroup = styled(CSSTransitionGroup)`
  display: contents;
`;

const FilterCol = styled(Col)`
  > * {
    margin-right: 1rem;
  }
  
  > *:last-of-type {
    margin-right: 0;
  }
`;

const Searchbar = styled(FormControl)`
  width: unset;
`;

const SelectButton = styled(PlainButton)`
  color: ${green};
  font-weight: 300;
  margin-right: 1rem;
`;

const Overlay = styled.div`
  position: absolute;
  background-color: #ffffffad;
  height: 100%;
  width: 100%;
  z-index: 98;
  text-align: center;
  padding: 2rem;
  font-weight: 600;
  
  div {
    background-color: white;
    display: inline-block;
    padding: 20px 50px;
    border-radius: 5px;
    box-shadow: ${boxShadow};
    color: #000000e0;
  }
`;

export const filterTypesEnum = Object.freeze({
  boolean: 1,
  search: 2,
  isRelevant: 3,
  properties: {
    1: {
      isFilterActive: (filterValue) => filterValue === true,
      filterFunction: (propertyValue) => propertyValue === true,
    },
    2: {
      isFilterActive: (filterValue) => filterValue === true,
      filterFunction: (propertyValue) => propertyValue === true,
    },
    3: {
      isFilterActive: (filterValue) => filterValue !== null,
      filterFunction: (propertyValue, activeFilter, interactiblePlantsByBeeId) => {
        // We need to do value.value since the activeFilter.value is
        // constructed like { value: '', label: '' }
        const selectedBeeId = activeFilter.value.value;
        const interactiblePlants = interactiblePlantsByBeeId[selectedBeeId];
        return interactiblePlants.includes(propertyValue);
      },
    },
  },
});

const getActiveFilters = (filters) => Object.keys(filters)
  .reduce((acc, key) => {
    const correspondingFilter = filters[key];

    const isActive = filterTypesEnum.properties[correspondingFilter.filterType]
      .isFilterActive(correspondingFilter.value);

    if (isActive) {
      acc.push({
        ...correspondingFilter,
        targetProperty: key,
      });
    }

    return acc;
  }, []);

const applyFilters = (data, activeFilters, search, interactiblePlantsByBeeId = {}) => data
  .filter((item) => activeFilters
    .every((activeFilter) => { // For every active filter
      // get filterFunction by type
      const { filterFunction } = filterTypesEnum.properties[activeFilter.filterType];
      // And perform filterFunction on the right property value

      if (activeFilter.filterType === filterTypesEnum.search) {
        return activeFilter.targetProperties
          .some((property) => item[property].toUpperCase()
            .includes(search.toUpperCase()));
      }
      return filterFunction(item[activeFilter.targetProperty], activeFilter, interactiblePlantsByBeeId);
    }));

const dataHasImpactProperty = (data) => (data.length > 0) && data[0].hasOwnProperty('impact');

const applySorting = (data) => {
  if (data.length === 0) {
    return data;
  }
  // If the object contains the key impact we want to sort on that
  if (dataHasImpactProperty(data)) {
    return data.sort((a, b) => b.impact - a.impact);
  }
  // Otherwise just return it, since the API sorts on dutchName automatically
  return data;
};

const FilterableList = ({
  data,
  isLoading,
  filterConfig,
  searchableByProperties,
  quickSelect,
  render,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState(filterConfig);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const interactiblePlantsByBeeId = useSelector(
    selectInteractibleplantsByBeeId,
  );

  const renderFilters = () => Object.entries(filters)
    .map(([property, filterObj]) => {
      switch (filterObj.filterType) {
        case filterTypesEnum.boolean:
          return (
            <BooleanFilter
              key={filterObj.label}
              label={filterObj.label}
              help={filterObj.help}
              handleClick={() => setFilters(((prevState) => ({
                ...prevState,
                [property]: {
                  ...prevState[property],
                  value: !filterObj.value,
                },
              })))}
              selected={filterObj.value}
            />
          );
        case filterTypesEnum.isRelevant:
          return (
            <IsRelevantFilter
              key={filterObj.label}
              label={filterObj.label}
              // reactSelect gives back { label: '', value: '' }
              handleChange={(reactSelectObj) => {
                setFilters(((prevState) => ({
                  ...prevState,
                  [property]: {
                    ...prevState[property],
                    value: reactSelectObj,
                  },
                })));
              }}
              value={filterObj.value}
            />
          );
        default:
          return null;
      }
    });

  const activeFilters = getActiveFilters(filters);
  if (searchableByProperties && debouncedSearchTerm !== '') {
    activeFilters.push({
      filterType: filterTypesEnum.search,
      targetProperties: searchableByProperties,
    });
  }

  const renderPagination = (itemsPerPage, filteredData, handlePageChange) => (
    <Pagination
      itemClass="page-item"
      linkClass="page-link"
      prevPageText="vorige"
      nextPageText="volgende"
      firstPageText="eerste"
      lastPageText="laatste"
      activePage={currentPage}
      itemsCountPerPage={itemsPerPage}
      totalItemsCount={filteredData.length}
      pageRangeDisplayed={5}
      onChange={handlePageChange}
    />
  );

  // eslint-disable-next-line no-shadow
  const renderSortedOnText = (data) => {
    if (!data) {
      return null;
    }

    return (
      <small className="mb-1 d-block">
        Op
        {' '}
        <i>
          {
          dataHasImpactProperty(data)
            ? 'gemiddelde voedseldekking'
            : 'alfabetische volgorde'
        }
        </i>
        {' '}
        gesorteerd
      </small>
    );
  };

  const itemsPerPage = 20;

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const getCurrentPageData = (sortedData) => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return sortedData.slice(indexOfFirstItem, indexOfLastItem);
  };

  const filteredData = React.useMemo(() => {
    const filtered = applyFilters(
      data, activeFilters, debouncedSearchTerm, interactiblePlantsByBeeId,
    );
    return applySorting(filtered);
  }, [
    data, activeFilters, debouncedSearchTerm, interactiblePlantsByBeeId,
  ]);

  const currentPageData = getCurrentPageData(filteredData);

  const handleSelectResults = () => quickSelect.handleSelectResults(filteredData);

  return (
    <>
      <Row className="mt-3">
        <Col>
          <SemiBold>Filters</SemiBold>
        </Col>
      </Row>
      <Row className="mb-3">
        <FilterCol md={12} className="align-items-center d-flex flex-wrap">
          {renderFilters()}
          {
            searchableByProperties && (
              <Searchbar onChange={(e) => setSearchTerm(e.target.value)} type="text" value={searchTerm} placeholder="Zoek" className="mt-2 mt-md-0 " />
            )
          }
        </FilterCol>
      </Row>
      <Row className="mb-3">
        <Col lg className="align-items-center d-flex">
          {
            renderPagination(itemsPerPage, filteredData, handlePageChange)
          }
        </Col>
        <Col lg className="d-flex align-items-center justify-content-lg-end mt-3 mt-lg-0">
          {
            quickSelect && quickSelect.handleDeselectAll && (
              <SelectButton onClick={quickSelect.handleDeselectAll}>
                Deselecteer alles (
                {quickSelect.selectedCount}
                )
              </SelectButton>
            )
          }
          {
            quickSelect && quickSelect.handleSelectResults && (
              <SelectButton onClick={handleSelectResults}>Selecteer resultaten</SelectButton>
            )
          }
          <SemiBold>
            {filteredData.length}
            {' '}
            resultaten
          </SemiBold>
        </Col>
      </Row>
      {/* todo: emptystate */}
      <Row>
        <Col>
          {
            renderSortedOnText(data)
          }
        </Col>
      </Row>
      <Row>
        { (isLoading) && <Overlay><div>Advies ophalen...</div></Overlay> }
        {currentPageData && (
          <>
            <StyledReactCSSTransitionGroup
              transitionName="fade"
              transitionEnterTimeout={500}
              transitionLeaveTimeout={300}
            >
              {render(currentPageData)}
            </StyledReactCSSTransitionGroup>
          </>

        )}
      </Row>
      <Row className="mb-5">
        <Col>
          {
            renderPagination(itemsPerPage, filteredData, handlePageChange)
          }
        </Col>
      </Row>
    </>
  );
};

FilterableList.propTypes = {
  filterConfig: PropTypes.objectOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      filterType: PropTypes.number.isRequired,
      value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool,
      ]).isRequired,
    }),
  ).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  data: PropTypes.array.isRequired,
  render: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  searchableByProperties: PropTypes.arrayOf(
    PropTypes.string,
  ),
  quickSelect: PropTypes.shape({
    handleDeselectAll: PropTypes.func,
    handleSelectResults: PropTypes.func,
    selectedCount: PropTypes.number,
  }),
};

FilterableList.defaultProps = {
  isLoading: false,
  searchableByProperties: null,
  quickSelect: null,
};

export default FilterableList;
